import { DatePipe } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { DEFAULT_DATE_FORMAT, UI_DATE_FORMAT, DateUtil } from '@app/shared/utils/date-util.service';

@Component({
  selector: 'app-ri-calendar',
  templateUrl: './ri-calendar.component.html',
  styleUrls: ['./ri-calendar.component.scss']
})
export class RiCalendarComponent implements OnInit, AfterContentChecked {
  @Input() datePicker;
  _dates: any;
  get dates() {
    return this._dates;
  }
  @Input() set dates(value) {
    this._dates = value;
    this.initializeDates();
  };
  @Input() reservedDates;
  @Input() itemDetails: any;
  @Input() reservationPage: boolean;
  @Input() calenderDisabled = false;
  @Output() onDateRangeSelected: EventEmitter<any> = new EventEmitter();
  @Output() onDateReset: EventEmitter<any> = new EventEmitter();
  hoveredDate: NgbDate;
  fromDate: NgbDate;
  toDate: NgbDate;
  minDate: NgbDate;
  maxDate: NgbDate;
  disableAll: any;
  reservedDatesArr = [];
  dateRange: any;
  pipe = new DatePipe('en-US');
  dateDisabled = false;

  constructor(
    public calendar: NgbCalendar,
    private cdref: ChangeDetectorRef,
    private dateUtil: DateUtil
  ) {
    this.markDisabled = this.markDisabled.bind(this);
    this.disableAll = (date: NgbDate) => calendar.getWeekday(date) >= 0;
  }

  markDisabled(date: NgbDate) {
    const yesterdayDate: Date = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    return (this.hasReservation(date)
      || this.dateUtil.parseNgbDate(date, DEFAULT_DATE_FORMAT) < yesterdayDate) || this.calenderDisabled;
  }

  ngOnInit(): void {
  }

  initializeDates() {
    if (this.dates) {
      const minDate: Date = this.dates.minDate;
      const maxDate: Date = this.dates.maxDate;

      this.minDate = this.dateUtil.createNgbDate(minDate || new Date());
      if (maxDate) {
        this.maxDate = this.dateUtil.createNgbDate(maxDate);
      }

      this.dateDisabled = this.minDate === this.maxDate;

      const initFromDate: Date = this.dates.initFromDate;
      const initToDate: Date = this.dates.initToDate;
      if (initFromDate) {
        const fromDate = this.dateUtil.createNgbDate(initFromDate);
        const toDate = this.dateUtil.createNgbDate(initToDate);
        if (initToDate
          && initToDate > initFromDate
          && initFromDate >= minDate
          && initToDate <= maxDate
        ) {
          this.dates.fromDate = fromDate;
          this.dates.toDate = toDate;
          this.fromDate = fromDate;
          this.toDate = toDate;
          this.setDateRange(
            this.dateUtil.formatNgbDate(fromDate, DEFAULT_DATE_FORMAT, UI_DATE_FORMAT),
            this.dateUtil.formatNgbDate(toDate, DEFAULT_DATE_FORMAT, UI_DATE_FORMAT)
          );
          this.onDateRangeSelected.emit({
            from: fromDate,
            to: toDate
          });
        }
      }
    }
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  onDateSelection(date: NgbDate, datePicker) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      if (this.checkIfDateInBetween() || this.checkIfDateReserved(date)) {
        this.restartDate(date);
        return;
      }

      this.dates.fromDate = this.fromDate;
      this.dates.toDate = this.toDate;
      const formatFromDate = this.dateUtil.formatNgbDate(this.fromDate, DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT);
      const formatToDate = this.dateUtil.formatNgbDate(this.toDate, DEFAULT_DATE_FORMAT, DEFAULT_DATE_FORMAT);

      this.onDateRangeSelected.emit({ from: this.fromDate, to: this.toDate });
      this.setDateRange(formatFromDate, formatToDate);

      if (!this.datePicker) {
        datePicker.close();
      }
    } else {
      this.restartDate(date);
    }

    console.log(this.dateRange);
  }

  getTooltip(date: NgbDate) {
    const holidayTooltip = this.hasReservation(date);

    if (holidayTooltip) {
      return 'Date Reserved';
    }
  }

  private restartDate(date) {
    if (this.reservationPage) {
      this.onDateReset.emit(true);
    }
    this.toDate = null;
    this.fromDate = date;
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      date.equals(this.toDate) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  hasReservation(date: NgbDate) {
    if (!this.reservationPage) {
      return false;
    }
    return this.checkIfDateReserved(date);
  }

  private checkIfDateInBetween() {
    let isBetween = false;
    for (let i = 0; i < this.reservedDatesArr.length; i++) {
      if (this.isRange(this.reservedDatesArr[i])) {
        isBetween = true;
      }
    }
    return isBetween;
  }

  storeDates(date: NgbDate) {
    const dateTime = new Date(date.year, date.month - 1, date.day);
    dateTime.setHours(0, 0, 0, 0);
    const dateTimestamp = dateTime.getTime();
    for (const key in this.reservedDates) {
      if (!this.reservedDates.hasOwnProperty(key)) {
        continue;
      }
      const rStartDate = new Date(this.reservedDates[key].startDate.seconds * 1000);
      rStartDate.setHours(0, 0, 0, 0);
      const rStartDateTimestamp = rStartDate.getTime();
      const rEndDate = new Date(this.reservedDates[key].endDate.seconds * 1000);
      rEndDate.setHours(0, 0, 0, 0);
      const rEndDateTimestamp = rEndDate.getTime();
      if (dateTimestamp >= rStartDateTimestamp && dateTimestamp <= rEndDateTimestamp) {
        this.reservedDatesArr.push(date);
        this.reservedDatesArr = this.getUnique(this.reservedDatesArr);
      }
    }
  }

  private getUnique(arr) {
    return arr.filter((e, i) => arr.indexOf(e) >= i);
  }

  checkIfDateReserved(date: NgbDate) {
    if (this.reservedDatesArr.indexOf(date) >= 0) {
      return true;
    }
  }

  redrawRange(start, end) {
    if (start && end) {
      this.toDate = end;
      this.fromDate = start;
      this.setDateRange(
        this.dateUtil.formatNgbDate(start, DEFAULT_DATE_FORMAT, UI_DATE_FORMAT),
        this.dateUtil.formatNgbDate(end, DEFAULT_DATE_FORMAT, UI_DATE_FORMAT)
      );
    }
  }

  setDateRange(fromDate: string, toDate: string): void {
    /* this.dateRange = this.pipe.transform(fromDate, 'mediumDate') + ' - ' + this.pipe.transform(toDate, 'mediumDate'); */
    this.cdref.detectChanges();
  }

  setDateData(date) {
    date = date || new Date();
    const res = this.dateUtil.formatNgbDate(date, DEFAULT_DATE_FORMAT, UI_DATE_FORMAT);
    return res === 'Invalid date' ? date : res;
  }
}
